import { Module } from 'vuex'
import type {
  User,
  Rule,
  Module as AuthModule,
  Company,
  AuthSession,
  AuthState,
  ParameterBilling,
  ParameterRental,
  ParameterStock,
  ParameterSystem,
} from '../types/auth.types'

const STORAGE_KEY = 'auth_data'
const PHOTO_BASE_URL = 'https://espaco.blob.core.windows.net/nixloc-photo-user/'
const PHOTO_DEFAULT = `${PHOTO_BASE_URL}noImageEdit.png`

function loadInitialState(): AuthState {
  const savedToken = localStorage.getItem('token')
  const savedData = localStorage.getItem(STORAGE_KEY)

  if (savedToken && savedData) {
    const data = JSON.parse(savedData)
    return {
      token: savedToken,
      user: data.user,
      company: data.company,
      modules: data.modules,
      rules: data.rules,
      parameterBilling: data.parameterBilling,
      parameterRental: data.parameterRental,
      parameterStock: data.parameterStock,
      parameterSystem: data.parameterSystem,
    }
  }

  return {
    token: null,
    user: null,
    company: null,
    modules: [],
    rules: [],
    parameterBilling: null,
    parameterRental: null,
    parameterStock: null,
    parameterSystem: null,
  }
}

const authStore: Module<AuthState, any> = {
  namespaced: true,

  state: loadInitialState,

  getters: {
    isAuthenticated: (state) => !!state.token,

    moduleNames: (state) => state.modules.map((m) => m.name),

    userPhotoUrl: (state) =>
      state.user?.photo ? `${PHOTO_BASE_URL}${state.user.photo}` : PHOTO_DEFAULT,

    hasCompanyModule: (state) => (name: string) =>
      state.modules.some((m) => m.name === name),

    hasUserModule: (state) => (name: string) =>
      state.rules.some((r) => r.name === name),
  },

  mutations: {
    SET_AUTH_DATA(state, content: AuthSession) {
      state.token = content.token

      const firstModule = content.user.modules[0]
      state.company = firstModule?.company ?? null

      state.modules = content.user.modules.map(({ company: _, ...mod }) => mod)
      state.rules = content.user.rules

      const { rules: _r, modules: _m, ...userData } = content.user
      state.user = { ...userData, rules: [], modules: [] }

      state.parameterBilling = content.parameterBilling
      state.parameterRental = content.parameterRental
      state.parameterStock = content.parameterStock
      state.parameterSystem = content.parameterSystem

      localStorage.setItem('token', content.token)
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          user: state.user,
          company: state.company,
          modules: state.modules,
          rules: state.rules,
          parameterBilling: state.parameterBilling,
          parameterRental: state.parameterRental,
          parameterStock: state.parameterStock,
          parameterSystem: state.parameterSystem,
        }),
      )
    },

    LOGOUT(state) {
      state.token = null
      state.user = null
      state.company = null
      state.modules = []
      state.rules = []
      state.parameterBilling = null
      state.parameterRental = null
      state.parameterStock = null
      state.parameterSystem = null
      localStorage.removeItem('token')
      localStorage.removeItem(STORAGE_KEY)
    },
  },

  actions: {
    setAuthData({ commit }, content: AuthSession) {
      commit('SET_AUTH_DATA', content)
    },

    logout({ commit }) {
      commit('LOGOUT')
    },
  },
}

export default authStore
